import { gql, useMutation, useQuery } from '@apollo/client';
import { IsinClassEnumType, IsinStatusEnumType } from '__generated__/graphql';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Select from 'primitives/Select';

import ErrorMessage from 'components/ErrorMessage';

type BulkStatusTarget = IsinClassEnumType | 'ALL';

type IsinState = Record<IsinClassEnumType, string>;
type AssignmentState = Record<string, IsinClassEnumType>;
type ClosingStatusState = Record<string, IsinStatusEnumType | undefined>;

const ISIN_CLASSES: IsinClassEnumType[] = [
  IsinClassEnumType.Z,
  IsinClassEnumType.L,
  IsinClassEnumType.X,
  IsinClassEnumType.Y,
];

const GET_DEAL_ISIN_CONFIGURATION = gql(`
  query GetDealIsinConfiguration($id: ID!, $closingLimit: Int) {
    deal(id: $id) {
      id
      status
      xClassIsinNumber
      lClassIsinNumber
      zClassIsinNumber
      yClassIsinNumber
      carryRecipients {
        id
        isAngelListIndia
        investmentEntity {
          id
        }
      }
      syndicate {
        id
        syndicateLeads {
          id
          role
          user {
            id
            name
            investmentEntities {
              id
              name
            }
          }
        }
      }
      closings(limit: $closingLimit) {
        nodes {
          id
          isinClass
          isinNumber
          isinStatus
          investmentEntity {
            id
            name
            user {
              id
              name
            }
          }
        }
        pageInfo {
          hasNextPage
        }
      }
    }
  }
`);

const UPDATE_DEAL_ISINS = gql(`
  mutation UpdateDealIsins(
    $id: ID!
    $xClassIsinNumber: String
    $lClassIsinNumber: String
    $zClassIsinNumber: String
    $yClassIsinNumber: String
    $xClosingIds: [ID!]!
    $lClosingIds: [ID!]!
    $zClosingIds: [ID!]!
    $yClosingIds: [ID!]!
  ) {
    updateDealIsins(
      id: $id
      xClassIsinNumber: $xClassIsinNumber
      lClassIsinNumber: $lClassIsinNumber
      zClassIsinNumber: $zClassIsinNumber
      yClassIsinNumber: $yClassIsinNumber
      xClosingIds: $xClosingIds
      lClosingIds: $lClosingIds
      zClosingIds: $zClosingIds
      yClosingIds: $yClosingIds
    ) {
      id
      xClassIsinNumber
      lClassIsinNumber
      zClassIsinNumber
      yClassIsinNumber
    }
  }
`);

const UPDATE_DEAL_ISIN_STATUSES = gql(`
  mutation UpdateDealIsinStatuses($id: ID!, $closingIds: [ID!]!, $isinStatus: IsinStatusEnumType!) {
    updateDealIsinStatuses(id: $id, closingIds: $closingIds, isinStatus: $isinStatus)
  }
`);

const ISIN_CLASS_OPTIONS = [
  { value: IsinClassEnumType.X, label: 'X class (Normal Class)' },
  { value: IsinClassEnumType.L, label: 'L class (GP Class)' },
  { value: IsinClassEnumType.Z, label: 'Z class (AngelList India Class)' },
  { value: IsinClassEnumType.Y, label: 'Y class' },
];

const BULK_STATUS_CLASS_OPTIONS = [{ value: 'ALL', label: 'All classes' }, ...ISIN_CLASS_OPTIONS];

const ISIN_STATUS_OPTIONS = [
  { value: IsinStatusEnumType.InProcess, label: 'In process', color: 'bg-yellow-400' },
  { value: IsinStatusEnumType.Credited, label: 'Credited', color: 'bg-green-500' },
  { value: IsinStatusEnumType.Debited, label: 'Debited', color: 'bg-amber-500' },
];

const ISIN_CLASS_COPY: Record<
  IsinClassEnumType,
  {
    title: string;
    description: string;
  }
> = {
  [IsinClassEnumType.X]: {
    title: 'X class',
    description: 'Other investor and LP closings',
  },
  [IsinClassEnumType.L]: {
    title: 'L class',
    description: 'Syndicate lead and GP closings',
  },
  [IsinClassEnumType.Z]: {
    title: 'Z class',
    description: 'AngelList India closings',
  },
  [IsinClassEnumType.Y]: {
    title: 'Y class',
    description: 'Additional manually assigned closings',
  },
};

const EDIT_DISABLED_STATUSES = ['CANCELLED', 'CLOSED', 'DELETED'];

const normalizeIsinNumber = (value?: string | null) => value?.trim() || '';

const IsinNumbers: FC = () => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };
  const [isinNumbers, setIsinNumbers] = useState<IsinState>({
    [IsinClassEnumType.X]: '',
    [IsinClassEnumType.L]: '',
    [IsinClassEnumType.Z]: '',
    [IsinClassEnumType.Y]: '',
  });
  const [assignments, setAssignments] = useState<AssignmentState>({});
  const [closingStatuses, setClosingStatuses] = useState<ClosingStatusState>({});
  const [bulkStatusTarget, setBulkStatusTarget] = useState<BulkStatusTarget>('ALL');
  const [bulkStatus, setBulkStatus] = useState<IsinStatusEnumType>(IsinStatusEnumType.InProcess);
  const [statusActionTarget, setStatusActionTarget] = useState<'bulk' | string | null>(null);

  const { loading, error, data, refetch } = useQuery(GET_DEAL_ISIN_CONFIGURATION, {
    variables: {
      id: dealId,
      closingLimit: 500,
    },
  });

  const [updateDealIsins, { loading: saveLoading, error: saveError }] = useMutation(
    UPDATE_DEAL_ISINS,
    {
      onCompleted: () => {
        refetch();
      },
    }
  );

  const [updateDealIsinStatuses, { loading: statusSaveLoading, error: statusSaveError }] =
    useMutation(UPDATE_DEAL_ISIN_STATUSES, {
      onCompleted: () => {
        setStatusActionTarget(null);
        refetch();
      },
      onError: () => {
        setStatusActionTarget(null);
      },
    });

  const closings = useMemo(() => {
    if (!data?.deal?.closings?.nodes) return [];

    return [...data.deal.closings.nodes].sort((closingA, closingB) => {
      const userA = closingA.investmentEntity.user.name || '';
      const userB = closingB.investmentEntity.user.name || '';
      return `${userA}-${closingA.investmentEntity.name}`.localeCompare(
        `${userB}-${closingB.investmentEntity.name}`
      );
    });
  }, [data]);

  useEffect(() => {
    if (!data?.deal) return;

    const angelListIndiaEntityIds = new Set<string>();
    const imEntityIds = new Set<string>();
    const syndicateLeadUserIds = new Set<string>();

    data.deal.syndicate.syndicateLeads.forEach(lead => {
      if (lead.role === 'ANGELLIST_INDIA') {
        lead.user.investmentEntities.forEach(entity => {
          angelListIndiaEntityIds.add(entity.id);
        });
        return;
      }

      syndicateLeadUserIds.add(lead.user.id);
      lead.user.investmentEntities.forEach(entity => {
        imEntityIds.add(entity.id);
      });
    });

    data.deal.carryRecipients.forEach(recipient => {
      if (!recipient.investmentEntity?.id) return;

      if (recipient.isAngelListIndia) {
        angelListIndiaEntityIds.add(recipient.investmentEntity.id);
        return;
      }

      imEntityIds.add(recipient.investmentEntity.id);
    });

    const nextAssignments = data.deal.closings.nodes.reduce(
      (acc: AssignmentState, closing: any) => {
        if (closing.isinClass !== null && closing.isinClass !== undefined) {
          acc[closing.id] = closing.isinClass;
          return acc;
        }

        if (angelListIndiaEntityIds.has(closing.investmentEntity.id)) {
          acc[closing.id] = IsinClassEnumType.Z;
          return acc;
        }

        if (
          imEntityIds.has(closing.investmentEntity.id) ||
          syndicateLeadUserIds.has(closing.investmentEntity.user.id)
        ) {
          acc[closing.id] = IsinClassEnumType.L;
          return acc;
        }

        acc[closing.id] = IsinClassEnumType.X;
        return acc;
      },
      {} as AssignmentState
    );

    setAssignments(nextAssignments);
    setClosingStatuses(
      data.deal.closings.nodes.reduce((acc: ClosingStatusState, closing: any) => {
        acc[closing.id] = closing.isinStatus || undefined;
        return acc;
      }, {} as ClosingStatusState)
    );
    setIsinNumbers({
      [IsinClassEnumType.X]: normalizeIsinNumber(data.deal.xClassIsinNumber),
      [IsinClassEnumType.L]: normalizeIsinNumber(data.deal.lClassIsinNumber),
      [IsinClassEnumType.Z]: normalizeIsinNumber(data.deal.zClassIsinNumber),
      [IsinClassEnumType.Y]: normalizeIsinNumber(data.deal.yClassIsinNumber),
    });
  }, [data]);

  const groupedClosingIds = useMemo(
    () => ({
      [IsinClassEnumType.X]: closings
        .filter(closing => assignments[closing.id] === IsinClassEnumType.X)
        .map(closing => closing.id),
      [IsinClassEnumType.L]: closings
        .filter(closing => assignments[closing.id] === IsinClassEnumType.L)
        .map(closing => closing.id),
      [IsinClassEnumType.Z]: closings
        .filter(closing => assignments[closing.id] === IsinClassEnumType.Z)
        .map(closing => closing.id),
      [IsinClassEnumType.Y]: closings
        .filter(closing => assignments[closing.id] === IsinClassEnumType.Y)
        .map(closing => closing.id),
    }),
    [assignments, closings]
  );

  if (loading) return <LoadingIndicator />;

  if (error || !data || !dealId) return <ErrorMessage error={error} refetch={refetch} />;

  const hasMoreClosings = data.deal.closings.pageInfo.hasNextPage;
  const disableEdit = EDIT_DISABLED_STATUSES.includes(data.deal.status) || hasMoreClosings;
  const bulkStatusClosingIds =
    bulkStatusTarget === 'ALL'
      ? closings.map(closing => closing.id)
      : groupedClosingIds[bulkStatusTarget];

  const handleSave = () =>
    updateDealIsins({
      variables: {
        id: dealId,
        xClassIsinNumber: normalizeIsinNumber(isinNumbers[IsinClassEnumType.X]) || null,
        lClassIsinNumber: normalizeIsinNumber(isinNumbers[IsinClassEnumType.L]) || null,
        zClassIsinNumber: normalizeIsinNumber(isinNumbers[IsinClassEnumType.Z]) || null,
        yClassIsinNumber: normalizeIsinNumber(isinNumbers[IsinClassEnumType.Y]) || null,
        xClosingIds: groupedClosingIds[IsinClassEnumType.X],
        lClosingIds: groupedClosingIds[IsinClassEnumType.L],
        zClosingIds: groupedClosingIds[IsinClassEnumType.Z],
        yClosingIds: groupedClosingIds[IsinClassEnumType.Y],
      },
    });

  const handleBulkStatusUpdate = () => {
    setStatusActionTarget('bulk');
    updateDealIsinStatuses({
      variables: {
        id: dealId,
        closingIds: bulkStatusClosingIds,
        isinStatus: bulkStatus,
      },
    });
  };

  const handleSingleStatusUpdate = (closingId: string) => {
    const isinStatus = closingStatuses[closingId];
    if (!isinStatus) return;

    setStatusActionTarget(closingId);
    updateDealIsinStatuses({
      variables: {
        id: dealId,
        closingIds: [closingId],
        isinStatus,
      },
    });
  };

  return (
    <div className="space-y-4">
      {hasMoreClosings && (
        <ErrorMessage
          type="alert"
          title="Only the first 500 closings are loaded"
          error={{
            message:
              'Increase the query limit before editing or saving ISIN assignments for larger deals.',
          }}
        />
      )}

      {saveError && (
        <ErrorMessage type="alert" title="Unable to save ISIN numbers" error={saveError} />
      )}

      {statusSaveError && (
        <ErrorMessage type="alert" title="Unable to update ISIN statuses" error={statusSaveError} />
      )}

      <div className="border border-gray-200 rounded-lg bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Bulk update ISIN status</h3>
            <p className="text-sm text-gray-500">
              Update the ISIN status for one class or all classes in a single action.
            </p>
          </div>
          {!disableEdit && (
            <Button
              loading={statusSaveLoading && statusActionTarget === 'bulk'}
              disabled={bulkStatusClosingIds.length === 0}
              onClick={handleBulkStatusUpdate}
            >
              Submit
            </Button>
          )}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Select
            label="ISIN class"
            value={bulkStatusTarget}
            readOnly={disableEdit}
            fullWidth
            options={BULK_STATUS_CLASS_OPTIONS}
            onChange={value => setBulkStatusTarget(value as BulkStatusTarget)}
          />
          <Select
            label="ISIN status"
            value={bulkStatus}
            readOnly={disableEdit}
            fullWidth
            options={ISIN_STATUS_OPTIONS}
            onChange={value => setBulkStatus(value as IsinStatusEnumType)}
          />
        </div>

        <p className="mt-3 text-sm text-gray-500">
          {bulkStatusClosingIds.length} closing{bulkStatusClosingIds.length === 1 ? '' : 's'} will
          be updated.
        </p>
      </div>

      {ISIN_CLASSES.map(isinClass => {
        const classClosings = closings.filter(closing => assignments[closing.id] === isinClass);

        return (
          <div key={isinClass} className="border border-gray-200 rounded-lg bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-base font-semibold text-gray-900">
                {ISIN_CLASS_COPY[isinClass].title}
              </h3>
              {!disableEdit && (
                <Button loading={saveLoading} onClick={handleSave}>
                  Save
                </Button>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">{ISIN_CLASS_COPY[isinClass].description}</p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                ISIN number
              </label>
              <input
                value={isinNumbers[isinClass]}
                onChange={event =>
                  setIsinNumbers(currentState => ({
                    ...currentState,
                    [isinClass]: event.target.value,
                  }))
                }
                disabled={disableEdit}
                className="block w-full max-w-xl rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-black disabled:bg-gray-100"
                placeholder={`Enter ${ISIN_CLASS_COPY[isinClass].title.toLowerCase()} ISIN`}
              />
            </div>

            <div className="mt-4 space-y-2">
              {classClosings.length === 0 && (
                <p className="text-sm text-gray-500">
                  No closings are currently assigned to this class.
                </p>
              )}

              {classClosings.map(closing => (
                <div key={closing.id} className="rounded-lg border border-gray-200 px-4 py-3">
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem] sm:items-end">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {closing.investmentEntity.user.name}
                      </p>
                      <p className="text-sm text-gray-500">{closing.investmentEntity.name}</p>
                    </div>
                    <div className="w-full">
                      <Select
                        label="ISIN class"
                        value={assignments[closing.id]}
                        readOnly={disableEdit}
                        fullWidth
                        options={ISIN_CLASS_OPTIONS}
                        onChange={value =>
                          setAssignments(currentState => ({
                            ...currentState,
                            [closing.id]: value as IsinClassEnumType,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                    <div className="w-full">
                      <Select
                        label="ISIN status"
                        value={closingStatuses[closing.id]}
                        readOnly={disableEdit}
                        fullWidth
                        placeholder="Select ISIN status"
                        options={ISIN_STATUS_OPTIONS}
                        onChange={value =>
                          setClosingStatuses(currentState => ({
                            ...currentState,
                            [closing.id]: value as IsinStatusEnumType,
                          }))
                        }
                      />
                    </div>
                    {!disableEdit && (
                      <Button
                        className="whitespace-nowrap sm:min-w-36"
                        loading={statusSaveLoading && statusActionTarget === closing.id}
                        disabled={!closingStatuses[closing.id]}
                        onClick={() => handleSingleStatusUpdate(closing.id)}
                      >
                        Update status
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IsinNumbers;
